import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  Switch,
  Typography,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
   Security as SecurityIcon
 } from '@mui/icons-material';
 // Import permission utils from the centralized store
 import {
    canManageTargetUser, // Use the refactored version from permissionStore
    canActivateTargetUser,
    canDeactivateTargetUser,
    canEditTargetUserRoles, // Import the new function
    isManagementPrimaryRole
 } from '../../utils/permissionStore';
 import { getStoredPermissions } from '../../utils/permissionStore'; // Import getter for current user permissions
 
 function UserTable({
   users,
   // currentUser prop might still be needed for basic info like ID for 'isSelf' check,
   // but permission checks should use the stored permissions object.
   currentUser,
  onEdit,
  onManage2FA,
  onDelete,
  onToggleRole,
  onToggleActive,
  onToggleOnDuty
}) {

  if (!users) {
    return <Typography>No user data available.</Typography>;
  }

  const activeUsers = users.filter(user => user.isActive !== false);
   const deactivatedUsers = users.filter(user => user.isActive === false);
 
   // Get current user's permissions once
   const storedPermissionsResult = getStoredPermissions();
   const currentUserPermissions = storedPermissionsResult?.permissions;
 
   const renderUserRow = (user, isDeactivated = false) => {
     // Determine if the current user can manage this specific user row using the central helper from permissionStore
     // Pass the stored permissions object and the target user row data
     const canManageThisUser = currentUserPermissions ? canManageTargetUser(currentUserPermissions, user) : false;
     // Specific check for self-management restrictions (currentUser prop might still be needed for ID)
      const isSelf = currentUser && (currentUser._id === user._id || currentUser.id === user.id);
  
      // Use specific permission functions from the store
      const canEditRoles = currentUserPermissions ? canEditTargetUserRoles(currentUserPermissions, user) : false;
      const isManagementUser = isManagementPrimaryRole(user.primaryRole || user.role);
      // const canEditStatus = currentUserPermissions ? (user.isActive === false ? canActivateTargetUser(currentUserPermissions, user) : canDeactivateTargetUser(currentUserPermissions, user)) : false; // Already handled below
      const canEditDuty = currentUserPermissions?.canEditUserDutyStatus ?? false; // Keep simple flag for now, or create specific util if needed
      const canEdit = currentUserPermissions?.canEditUsers ?? false; // General edit permission
     const canManage2FA = currentUserPermissions?.canManageUser2FA ?? false;
     // const canDelete = currentUserPermissions?.canDeleteUsers ?? false; // Replaced by canDeactivate check for the button


    return (
    <TableRow
      key={user.id || user._id}
      sx={isDeactivated ? { backgroundColor: '#f5f5f5' } : {}}
    >
      <TableCell>{user.firstName || user.ownerFirstName}</TableCell>
      <TableCell>{user.lastName || user.ownerLastName}</TableCell>
      <TableCell>{user.phone}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>{user.username}</TableCell>
      <TableCell>{user.primaryRole || user.role}</TableCell>
      <TableCell>
        <Checkbox
          checked={user.secondaryRoles?.admin || user.isAdmin || false}
           onChange={() => onToggleRole(user.id || user._id, 'admin')}
           disabled={isDeactivated || !canEditRoles || isSelf} // Use specific role edit permission
         />
       </TableCell>
       <TableCell>
         <Checkbox
           checked={user.secondaryRoles?.dispatcher || user.isDispatcher || false}
           onChange={() => onToggleRole(user.id || user._id, 'dispatcher')}
           disabled={isDeactivated || !canEditRoles || isSelf} // Use specific role edit permission
         />
       </TableCell>
       <TableCell>
         <Checkbox
           checked={isManagementUser || user.secondaryRoles?.driver || user.isDriver || false}
           onChange={() => onToggleRole(user.id || user._id, 'driver')}
           disabled={isManagementUser || isDeactivated || !canEditRoles || isSelf} // Use specific role edit permission
         />
       </TableCell>
       <TableCell>
         <Checkbox
           checked={user.secondaryRoles?.answeringService || user.isAnsweringService || false}
           onChange={() => onToggleRole(user.id || user._id, 'answeringService')}
           disabled={isDeactivated || !canEditRoles || isSelf} // Use specific role edit permission
         />
      </TableCell>
       <TableCell>
         <Checkbox
           checked={user.isActive !== false}
           onChange={() => onToggleActive(user.id || user._id)}
           // Use specific activation/deactivation permission checks
           disabled={
             isSelf || // Cannot change own status
             !currentUserPermissions || // No permissions loaded
             (user.isActive === false && !canActivateTargetUser(currentUserPermissions, user)) || // Cannot activate
             (user.isActive !== false && !canDeactivateTargetUser(currentUserPermissions, user)) // Cannot deactivate
           }
         />
       </TableCell>
       <TableCell>
        {(isManagementUser || user.secondaryRoles?.driver || user.isDriver) && (
          <Switch
            checked={user.isOnDuty || false}
            onChange={() => onToggleOnDuty(user.id || user._id)}
            // Drivers go on and off shift themselves, so your own switch is always yours
            disabled={isDeactivated || !(isSelf || (canEditDuty && canManageThisUser))}
          />
        )}
      </TableCell>
      <TableCell align="right">
        <IconButton
          edge="end"
          aria-label="edit"
          onClick={() => onEdit(user)}
          disabled={!canEdit || (!canManageThisUser && !isSelf)} // Use central permission
        >
          <EditIcon />
        </IconButton>
        <IconButton
          edge="end"
          aria-label="2fa"
          onClick={() => onManage2FA(user)}
          disabled={!canManage2FA} // Use central permission
        >
          <SecurityIcon />
         </IconButton>
         {/* Conditionally render delete/deactivate icon based on permissions */}
         {/* Use canDeactivate permission. Backend handles permanent delete logic. */}
         {currentUserPermissions && canDeactivateTargetUser(currentUserPermissions, user) && !isSelf &&
            <IconButton
              edge="end"
              aria-label={isDeactivated ? "permanently delete" : "deactivate"} // Label might need adjustment based on backend logic visibility
             onClick={() => onDelete(user.id || user._id)}
             color="error" // Always error color for delete/deactivate
             // Tooltip might be useful here: title={isDeactivated ? "Permanently Delete" : "Deactivate User"}
           >
             <DeleteIcon />
           </IconButton>
        }
      </TableCell>
    </TableRow>
   )};

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>Primary Role</TableCell>
            <TableCell>Admin</TableCell>
            <TableCell>Dispatcher</TableCell>
            <TableCell>Driver</TableCell>
            <TableCell>Answering Service</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>On Duty</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Active Users Section */}
          {activeUsers.map(user => renderUserRow(user, false))}

          {/* Separator for Deactivated Users */}
          {deactivatedUsers.length > 0 && (
            <TableRow>
              <TableCell colSpan={13} sx={{ py: 3 }}>
                <Typography variant="h6" color="textSecondary" align="center">
                  Deactivated Users
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {/* Deactivated Users Section */}
          {deactivatedUsers.map(user => renderUserRow(user, true))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default UserTable;
